import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { InputStyles } from './InputStyles';
import { IconEye, IconEyeOff } from '@/components/icons/StatusIcons';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import type { AppTheme } from '@/theme/index';

interface InputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  isEdit?: boolean;
  error?: string;
  isFocused?: boolean;
  onFocusChange?: (focused: boolean) => void;
  id?: string;
  name?: string;
  minLength?: number;
  maxLength?: number;
  hint?: string;
  type?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  width?: string | number;
  /** type="date"/"number" 등에서 사용하는 최소/최대 값 */
  min?: string | number;
  max?: string | number;
}

const StyledInput = styled.input<{
  disabled?: boolean;
  hasError?: boolean;
  theme?: AppTheme;
  $size?: 'xs' | 'sm' | 'md' | 'lg';
  $fullWidth?: boolean;
  $width?: string | number;
  $hasToggle?: boolean;
}>`
  width: ${({ $width }) => {
    if ($width !== undefined) {
      return typeof $width === 'number' ? `${$width}px` : $width;
    }
    return '100%';
  }};
  height: 26px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.surface.control.default};
  border: 1px solid
    ${({ hasError, theme }) =>
      hasError ? theme.colors.border.feedback.error : theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  text-align: left;
  padding: ${({ theme }) => theme.spacing[2]}px;
  padding-right: ${({ $hasToggle, theme }) => ($hasToggle ? '28px' : `${theme.spacing[2]}px`)};

  &::placeholder {
    text-align: right;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  &:focus {
    border: 1px solid
      ${({ hasError, theme }) =>
        hasError ? theme.colors.border.feedback.error : theme.colors.border.indicator};
    box-shadow: 0 0 0 2px
      ${({ hasError, theme }) =>
        hasError
          ? `${theme.colors.status.danger.stroke.border}33`
          : `${theme.colors.border.indicator}33`};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surface.control.disabled};
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
  }
`;

const StyledTextarea = styled.textarea<{
  disabled?: boolean;
  hasError?: boolean;
  theme?: AppTheme;
  $size?: 'xs' | 'sm' | 'md' | 'lg';
  $fullWidth?: boolean;
  $width?: string | number;
}>`
  width: ${({ $width }) => {
    if ($width !== undefined) {
      return typeof $width === 'number' ? `${$width}px` : $width;
    }
    return '100%';
  }};
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.surface.control.default};
  border: 1px solid
    ${({ hasError, theme }) =>
      hasError ? theme.colors.status.danger.stroke.border : theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  resize: vertical;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  text-align: left;

  ${({ $size = 'sm', theme }) => {
    const sizeMap = {
      xs: { height: 20, paddingX: theme.spacing[4], fontSize: theme.fontSize.xs },
      sm: { height: 24, paddingX: theme.spacing[6], fontSize: theme.fontSize.md },
      md: { height: 32, paddingX: theme.spacing[6], fontSize: theme.fontSize.md },
      lg: { height: 36, paddingX: theme.spacing[7], fontSize: theme.fontSize.md },
    };
    const config = sizeMap[$size];
    return `
      height: ${config.height}px;
      padding: ${theme.spacing[4]}px ${config.paddingX}px;
      font-size: ${config.fontSize}px;
      min-height: 80px;
      height: auto;
    `;
  }}

  &::placeholder {
    text-align: right;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  &:focus {
    border: 1px solid
      ${({ hasError, theme }) =>
        hasError ? theme.colors.status.danger.stroke.border : theme.colors.border.indicator};
    box-shadow: 0 0 0 2px
      ${({ hasError, theme }) =>
        hasError
          ? `${theme.colors.status.danger.stroke.border}33`
          : `${theme.colors.border.indicator}33`};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surface.control.disabled};
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  right: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  background: ${({ theme }) => theme.colors.surface.feedback.error};
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm}px;
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  z-index: 10;
  line-height: 1.2;

  /* 말풍선 꼬리 (삼각형) */
  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    right: 8px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${({ theme }) => theme.colors.surface.feedback.error};
  }
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const HintMessage = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  right: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  background: ${({ theme }) => theme.colors.surface.feedback.helper};
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm}px;
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.1);
  font-style: italic;
  white-space: nowrap;
  z-index: 10;
  line-height: 1.2;

  /* 말풍선 꼬리 (삼각형) */
  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    right: 8px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${({ theme }) => theme.colors.surface.feedback.helper};
  }
`;

export const Input: React.FC<InputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  multiline = false,
  rows = 3,
  isEdit = true,
  error,
  onFocusChange,
  id,
  name,
  minLength,
  maxLength,
  hint,
  type = 'text',
  size = 'sm',
  fullWidth = false,
  width,
  min,
  max,
}) => {
  const { t } = useTranslation();
  const isDisabled = disabled || !isEdit;
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);

  // 고유 ID 생성 (id가 없으면 label 기반으로 생성)
  const inputId = id || (label ? `input-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);

  const isPasswordField = type === 'password';
  const resolvedType = isPasswordField && isPasswordVisible ? 'text' : type;

  return (
    <InputStyles.InputContainer>
      <InputStyles.StyledInputRow>
        {label && <InputStyles.Label htmlFor={inputId}>{label}</InputStyles.Label>}

        <InputStyles.InputWrapper
          fullWidth={fullWidth || width !== undefined}
          hasLabel={!!label}
          style={{ position: 'relative' }}
        >
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {isFocused && !error && hint && <HintMessage>{hint}</HintMessage>}
          {multiline ? (
            <StyledTextarea
              id={inputId}
              name={name || inputId}
              disabled={isDisabled}
              value={value}
              onChange={e => onChange(e.target.value)}
              placeholder={placeholder}
              rows={rows}
              minLength={minLength}
              maxLength={maxLength}
              hasError={!!error}
              $size={size}
              $fullWidth={fullWidth}
              $width={width}
              onFocus={() => {
                onFocusChange?.(true);
              }}
              onBlur={() => {
                onFocusChange?.(false);
              }}
            />
          ) : (
            <StyledInput
              id={inputId}
              name={name || inputId}
              type={resolvedType}
              disabled={isDisabled}
              value={value}
              onChange={e => onChange(e.target.value)}
              placeholder={placeholder}
              minLength={minLength}
              maxLength={maxLength}
              min={min}
              max={max}
              hasError={!!error}
              $size={size}
              $fullWidth={fullWidth}
              $width={width}
              $hasToggle={isPasswordField}
              onFocus={() => {
                setIsFocused(true);
                onFocusChange?.(true);
              }}
              onBlur={() => {
                setIsFocused(false);
                onFocusChange?.(false);
              }}
            />
          )}
          {isPasswordField && (
            <PasswordToggleButton
              type="button"
              tabIndex={-1}
              disabled={isDisabled}
              onClick={() => setIsPasswordVisible(prev => !prev)}
              aria-label={
                isPasswordVisible ? t(i18nKeys.common.hide_password) : t(i18nKeys.common.show_password)
              }
            >
              {isPasswordVisible ? <IconEyeOff /> : <IconEye />}
            </PasswordToggleButton>
          )}
        </InputStyles.InputWrapper>
      </InputStyles.StyledInputRow>
    </InputStyles.InputContainer>
  );
};
